import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { ProductListSortOption, parseProductListSortOption } from '../../../utils/productListSort';
import { FilterOptions, parseFilterOptions } from '../../../utils/productListFilters';
import { RelewiseSearchService } from '../services/SearchService';
import { CategoryProductsResponse } from '../types/mapped';
import { createEmptyProductListResponse } from '../utils';
import { DEFAULT_PAGE_SIZE, NUMBER_OF_VARIANTS_PER_PRODUCT_IN_PLP } from '../const';

const FIRST_PAGE = 1;
const DECIMAL_RADIX = 10;

export class GetSearchProductsDataInput implements IActionInput {
    public query: string;
    public itemsPerPage: number;
    public variantsPerProduct: number;
    public page: number;
    public sort: ProductListSortOption;
    public filterOptions?: FilterOptions;

    public constructor(
        query: string,
        itemsPerPage: number,
        variantsPerProduct: number,
        page: number,
        sort: ProductListSortOption,
        filterOptions?: FilterOptions
    ) {
        this.query = query;
        this.itemsPerPage = itemsPerPage;
        this.variantsPerProduct = variantsPerProduct;
        this.page = page;
        this.sort = sort;
        this.filterOptions = filterOptions;
    }

    public getCacheKey = () =>
        `RelewiseSearchProducts-${this.query}-${this.itemsPerPage}-${this.page}-${this.sort}-${JSON.stringify(this.filterOptions ?? {})}`;

    public getCacheObjectType = () => 'RelewiseSearchProducts';

    public dataCacheType = (): CacheType => 'request';
}

const createInput = (inputData: ICreateActionContext<IGeneric<IAny>>): GetSearchProductsDataInput => {
    const query = (inputData.requestContext.query?.q ?? '').trim();
    const itemsPerPage = (inputData.config?.itemsPerPage as number | undefined) ?? DEFAULT_PAGE_SIZE;
    const variantsPerProduct = (inputData.config?.variantsPerProduct as number | undefined) ?? NUMBER_OF_VARIANTS_PER_PRODUCT_IN_PLP;

    const rawQuery = inputData.requestContext.query ?? {};
    const rawPage = parseInt(rawQuery.page ?? String(FIRST_PAGE), DECIMAL_RADIX);
    const page = isNaN(rawPage) || rawPage < FIRST_PAGE ? FIRST_PAGE : rawPage;
    const sort = parseProductListSortOption(rawQuery.sort);

    const filterOptions = parseFilterOptions(k => rawQuery[k]);

    return new GetSearchProductsDataInput(query, itemsPerPage, variantsPerProduct, page, sort, filterOptions);
};

export async function getSearchProductsData(input: GetSearchProductsDataInput, ctx: IActionContext): Promise<CategoryProductsResponse> {
    const emptyResponse = createEmptyProductListResponse({ itemsPerPage: input.itemsPerPage, currentPage: input.page });

    // Empty query is a normal state (e.g. landing on /search with no q) - not an error.
    if (!input.query) {
        return emptyResponse;
    }

    const searchService = new RelewiseSearchService(ctx.requestContext, ctx.telemetry);
    const result = await searchService.getSearchProducts(
        input.query,
        input.itemsPerPage,
        input.page,
        input.filterOptions,
        input.sort,
        input.variantsPerProduct
    );
    return result ?? emptyResponse;
}

export const getSearchProductsDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/custom/get-search-products',
    action: <IAction<CategoryProductsResponse>>getSearchProductsData,
    input: createInput
});

export default getSearchProductsDataAction;
